/**
 * RTWO Node Infrastructure — Domain Models
 * Phase 10 | Module ID: RTWO-NODE-001
 * Version: 1.0.0-phase10
 *
 * Strongly typed domain models for the RTWO Node Infrastructure.
 * Defines the enterprise node management layer for node lifecycle,
 * configuration, capabilities, health sources, assignments, and lifecycle records.
 *
 * Truthful Data Policy:
 * - No fake nodes, IP addresses, health, uptime, CPU, RAM, storage, or synchronization.
 * - No nodes shown as operational without real infrastructure.
 * - All runtime values are null until real node backend services are connected.
 *
 * Source: Node Infrastructure (RTWO-NODE-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type NodeId = string;
export type ConfigurationId = string;
export type CapabilityId = string;
export type HealthSourceId = string;
export type AssignmentId = string;
export type RecordId = string;
export type ValidatorId = string;
export type ServiceId = string;
export type NodeTimestamp = number | null;

export const NODE_INFRASTRUCTURE_VERSION = {
  moduleId: 'RTWO-NODE-001',
  moduleName: 'Node Infrastructure',
  version: '1.0.0-phase10',
  phase: 'Phase 10 — Node Infrastructure',
  status: 'Structure Complete — Waiting for Infrastructure',
  productionReadiness: 'Pending Infrastructure',
  policy:
    'Truthful Data Policy Enforced — No Fake Nodes, IP Addresses, Health, Uptime, CPU, RAM, Storage, or Synchronization',
} as const;

// ─────────────────────────────────────────────
// NODE TYPE
// ─────────────────────────────────────────────

/**
 * Supported RTWO Node Types
 */
export type RTWONodeType =
  | 'validator_node'
  | 'full_node'
  | 'archive_node'
  | 'rpc_node'
  | 'boot_node'
  | 'indexer_node'
  | 'observer_node'
  | 'development_node';

export interface NodeTypeDescriptor {
  nodeType: RTWONodeType;
  label: string;
  description: string;
  requiresValidator: boolean;
  requiresFullHistory: boolean;
  externallyAccessible: boolean;
  productionReady: boolean;
}

export const NODE_TYPES: NodeTypeDescriptor[] = [
  {
    nodeType: 'validator_node',
    label: 'Validator Node',
    description:
      'Participates in consensus. Requires approved validator identity and stake reference. Must be connected to real validator infrastructure.',
    requiresValidator: true,
    requiresFullHistory: false,
    externallyAccessible: false,
    productionReady: false,
  },
  {
    nodeType: 'full_node',
    label: 'Full Node',
    description:
      'Maintains full chain state from genesis. Validates all blocks and transactions. Does not participate in consensus.',
    requiresValidator: false,
    requiresFullHistory: true,
    externallyAccessible: true,
    productionReady: false,
  },
  {
    nodeType: 'archive_node',
    label: 'Archive Node',
    description:
      'Stores complete historical chain data including all state transitions. Required for historical queries and analytics.',
    requiresValidator: false,
    requiresFullHistory: true,
    externallyAccessible: true,
    productionReady: false,
  },
  {
    nodeType: 'rpc_node',
    label: 'RPC Node',
    description:
      'Exposes JSON-RPC and WebSocket API endpoints for external client access. Requires RPC Platform (Phase 11).',
    requiresValidator: false,
    requiresFullHistory: false,
    externallyAccessible: true,
    productionReady: false,
  },
  {
    nodeType: 'boot_node',
    label: 'Boot Node',
    description:
      'Entry point for new peers joining the network. Provides initial peer discovery. Does not store chain state.',
    requiresValidator: false,
    requiresFullHistory: false,
    externallyAccessible: true,
    productionReady: false,
  },
  {
    nodeType: 'indexer_node',
    label: 'Indexer Node',
    description:
      'Indexes chain data for efficient querying. Powers explorer and analytics platforms.',
    requiresValidator: false,
    requiresFullHistory: true,
    externallyAccessible: false,
    productionReady: false,
  },
  {
    nodeType: 'observer_node',
    label: 'Observer Node',
    description:
      'Monitors chain activity without participating in consensus or storing full history. Lightweight monitoring node.',
    requiresValidator: false,
    requiresFullHistory: false,
    externallyAccessible: false,
    productionReady: false,
  },
  {
    nodeType: 'development_node',
    label: 'Development Node',
    description:
      'Local development and testing node. Not connected to mainnet or testnet. For developer use only.',
    requiresValidator: false,
    requiresFullHistory: false,
    externallyAccessible: false,
    productionReady: false,
  },
];

export const NODE_TYPE_LABELS: Record<RTWONodeType, string> = {
  validator_node: 'Validator Node',
  full_node: 'Full Node',
  archive_node: 'Archive Node',
  rpc_node: 'RPC Node',
  boot_node: 'Boot Node',
  indexer_node: 'Indexer Node',
  observer_node: 'Observer Node',
  development_node: 'Development Node',
};

// ─────────────────────────────────────────────
// NODE STATUS (LIFECYCLE)
// ─────────────────────────────────────────────

/**
 * Node Lifecycle:
 * Created → Configured → Provisioned → Ready → Connected → Synchronizing → Operational → Maintenance → Retired
 *
 * TRUTHFUL DATA POLICY: "Operational" status must NEVER be set without real infrastructure.
 */
export type RTWONodeStatus =
  | 'created'
  | 'configured'
  | 'provisioned'
  | 'ready'
  | 'connected'
  | 'synchronizing'
  | 'operational'
  | 'maintenance'
  | 'retired';

export interface NodeLifecycleStage {
  status: RTWONodeStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresRealInfrastructure: boolean;
  truthfulNote: string;
}

export const NODE_LIFECYCLE: NodeLifecycleStage[] = [
  {
    status: 'created',
    label: 'Created',
    description:
      'Node record has been created in the registry. Identity assigned. No infrastructure provisioned yet.',
    terminal: false,
    requiresRealInfrastructure: false,
    truthfulNote: 'Node exists as a registry record only. No cloud resources allocated.',
  },
  {
    status: 'configured',
    label: 'Configured',
    description:
      'Node configuration has been applied. Storage provider, network provider, and RPC provider assigned.',
    terminal: false,
    requiresRealInfrastructure: false,
    truthfulNote: 'Configuration is a record only. No actual infrastructure configured.',
  },
  {
    status: 'provisioned',
    label: 'Provisioned',
    description:
      'Cloud or physical infrastructure has been provisioned. Resources allocated. Awaiting readiness check.',
    terminal: false,
    requiresRealInfrastructure: true,
    truthfulNote: 'Requires real cloud deployment. Cannot be set without real infrastructure.',
  },
  {
    status: 'ready',
    label: 'Ready',
    description: 'Node infrastructure is ready. All services started. Awaiting network connection.',
    terminal: false,
    requiresRealInfrastructure: true,
    truthfulNote: 'Requires real infrastructure to be provisioned and health-checked.',
  },
  {
    status: 'connected',
    label: 'Connected',
    description: 'Node has established P2P network connections. Peer discovery active.',
    terminal: false,
    requiresRealInfrastructure: true,
    truthfulNote: 'Requires real P2P network connections. Cannot be simulated.',
  },
  {
    status: 'synchronizing',
    label: 'Synchronizing',
    description: 'Node is synchronizing chain state from peers. Block sync in progress.',
    terminal: false,
    requiresRealInfrastructure: true,
    truthfulNote: 'Requires real blockchain data from real peers. No fake sync progress.',
  },
  {
    status: 'operational',
    label: 'Operational',
    description: 'Node is fully synchronized and operational. Participating in its assigned role.',
    terminal: false,
    requiresRealInfrastructure: true,
    truthfulNote:
      'CRITICAL: Must NEVER be set without real infrastructure and verified sync completion.',
  },
  {
    status: 'maintenance',
    label: 'Maintenance',
    description: 'Node is temporarily offline for maintenance, upgrades, or configuration changes.',
    terminal: false,
    requiresRealInfrastructure: true,
    truthfulNote: 'Maintenance state requires a previously operational node.',
  },
  {
    status: 'retired',
    label: 'Retired',
    description: 'Node has been permanently decommissioned. Retained for audit purposes.',
    terminal: true,
    requiresRealInfrastructure: false,
    truthfulNote: 'Retired nodes are read-only. All operations permanently blocked.',
  },
];

export const NODE_STATUS_LABELS: Record<RTWONodeStatus, string> = {
  created: 'Created',
  configured: 'Configured',
  provisioned: 'Provisioned',
  ready: 'Ready',
  connected: 'Connected',
  synchronizing: 'Synchronizing',
  operational: 'Operational',
  maintenance: 'Maintenance',
  retired: 'Retired',
};

export const NODE_STATUS_COLORS: Record<RTWONodeStatus, string> = {
  created: 'text-slate-400 bg-slate-400/10 border-slate-400/20',
  configured: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  provisioned: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  ready: 'text-teal-400 bg-teal-400/10 border-teal-400/20',
  connected: 'text-indigo-400 bg-indigo-400/10 border-indigo-400/20',
  synchronizing: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  operational: 'text-green-400 bg-green-400/10 border-green-400/20',
  maintenance: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  retired: 'text-slate-600 bg-slate-600/10 border-slate-600/20',
};

// ─────────────────────────────────────────────
// CONFIGURATION STATUS
// ─────────────────────────────────────────────

export type NodeConfigurationStatus =
  'not_configured' | 'pending' | 'applied' | 'failed' | 'outdated';

// ─────────────────────────────────────────────
// CAPABILITY TYPE & STATUS
// ─────────────────────────────────────────────

export type NodeCapabilityType =
  | 'consensus_participation'
  | 'block_validation'
  | 'state_storage'
  | 'archive_storage'
  | 'rpc_endpoint'
  | 'peer_discovery'
  | 'chain_indexing'
  | 'chain_monitoring'
  | 'sync_provider'
  | 'boot_service';

export type NodeCapabilityStatus = 'not_enabled' | 'enabled' | 'active' | 'degraded' | 'disabled';

// ─────────────────────────────────────────────
// HEALTH SOURCE TYPE & STATUS
// ─────────────────────────────────────────────

export type NodeHealthSourceType =
  | 'not_configured'
  | 'cloud_provider'
  | 'monitoring_agent'
  | 'p2p_probe'
  | 'rpc_probe'
  | 'internal_watchdog';

export type NodeHealthSourceStatus =
  'not_configured' | 'not_connected' | 'waiting_for_data' | 'connected' | 'error';

// ─────────────────────────────────────────────
// ASSIGNMENT STATUS
// ─────────────────────────────────────────────

export type NodeAssignmentStatus = 'pending' | 'active' | 'inactive' | 'revoked';

// ─────────────────────────────────────────────
// SYNC MODE
// ─────────────────────────────────────────────

export type NodeSyncMode = 'not_configured' | 'full' | 'fast' | 'snap' | 'light' | 'archive';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWONode
// ─────────────────────────────────────────────

/**
 * RTWONode — Core node record.
 *
 * Truthful Data Policy:
 * - networkAddress is null until real infrastructure is provisioned.
 * - status must NEVER be "operational" without real infrastructure.
 * - No fake IP addresses, hostnames, or network endpoints.
 */
export interface RTWONode {
  nodeId: NodeId;
  nodeName: string | null;
  nodeType: RTWONodeType;
  networkAddress: null; // Always null — Truthful Data Policy: no fake IP addresses
  status: RTWONodeStatus;
  version: string | null;
  region: string | null;
  createdAt: NodeTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWONodeConfiguration
// ─────────────────────────────────────────────

/**
 * RTWONodeConfiguration — Configuration record for a node.
 *
 * Truthful Data Policy:
 * - All provider fields are null until real infrastructure is configured.
 * - No fake storage, network, or RPC provider values.
 */
export interface RTWONodeConfiguration {
  configurationId: ConfigurationId;
  nodeId: NodeId;
  storageProvider: null; // null — Truthful Data Policy: no fake storage provider
  networkProvider: null; // null — Truthful Data Policy: no fake network provider
  rpcProvider: null; // null — Truthful Data Policy: no fake RPC provider
  syncMode: NodeSyncMode;
  status: NodeConfigurationStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWONodeCapability
// ─────────────────────────────────────────────

/**
 * RTWONodeCapability — A specific capability of a node.
 *
 * Capabilities are defined by node type and enabled only when
 * real infrastructure is provisioned and verified.
 */
export interface RTWONodeCapability {
  capabilityId: CapabilityId;
  nodeId: NodeId;
  capabilityType: NodeCapabilityType;
  enabled: boolean;
  status: NodeCapabilityStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWONodeHealthSource
// ─────────────────────────────────────────────

/**
 * RTWONodeHealthSource — Health data source for a node.
 *
 * Truthful Data Policy:
 * - No fake health, uptime, CPU, RAM, or storage metrics.
 * - All health sources are "not_configured" until real monitoring is connected.
 */
export interface RTWONodeHealthSource {
  healthSourceId: HealthSourceId;
  nodeId: NodeId;
  sourceType: NodeHealthSourceType;
  source: null; // null — Truthful Data Policy: no fake health source endpoints
  status: NodeHealthSourceStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWONodeAssignment
// ─────────────────────────────────────────────

/**
 * RTWONodeAssignment — Assigns a node to a validator or service.
 *
 * A node may be assigned to a validator (for validator nodes)
 * or to a service (for RPC, indexer, or observer nodes).
 */
export interface RTWONodeAssignment {
  assignmentId: AssignmentId;
  nodeId: NodeId;
  validatorId: ValidatorId | null; // null if assigned to a service
  serviceId: ServiceId | null; // null if assigned to a validator
  status: NodeAssignmentStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWONodeLifecycleRecord
// ─────────────────────────────────────────────

/**
 * RTWONodeLifecycleRecord — Immutable audit record of node status transitions.
 *
 * Every status change creates an immutable lifecycle record.
 * Records are retained permanently for audit and compliance.
 */
export interface RTWONodeLifecycleRecord {
  recordId: RecordId;
  nodeId: NodeId;
  previousStatus: RTWONodeStatus | null;
  newStatus: RTWONodeStatus;
  reason: string;
  timestamp: NodeTimestamp;
}

// ─────────────────────────────────────────────
// NODE ENGINE STATUS
// ─────────────────────────────────────────────

export type NodeEngineStatus =
  | 'not_initialized'
  | 'initializing'
  | 'structure_ready'
  | 'waiting_for_infrastructure'
  | 'waiting_for_cloud_deployment'
  | 'error'
  | 'shutdown';

export interface RTWONodeEngineStatus {
  engineId: 'RTWO-NODE-001';
  engineName: 'Node Infrastructure';
  version: string;
  status: NodeEngineStatus;
  registeredNodes: 0; // Always 0 — Truthful Data Policy: no fake nodes
  operationalNodes: 0; // Always 0 — Truthful Data Policy: no fake operational nodes
  totalCapabilities: number;
  source: 'Node Infrastructure — Waiting for Infrastructure';
  truthfulNote: string;
}

export const NODE_ENGINE_INITIAL_STATUS: RTWONodeEngineStatus = {
  engineId: 'RTWO-NODE-001',
  engineName: 'Node Infrastructure',
  version: NODE_INFRASTRUCTURE_VERSION.version,
  status: 'waiting_for_infrastructure',
  registeredNodes: 0,
  operationalNodes: 0,
  totalCapabilities: 0,
  source: 'Node Infrastructure — Waiting for Infrastructure',
  truthfulNote: 'No Nodes Registered. Waiting For Infrastructure. Node Architecture Ready.',
};

// ─────────────────────────────────────────────
// SERVICE IDS
// ─────────────────────────────────────────────

export const NODE_SERVICE_IDS = {
  NODE_REGISTRY: 'rtwo-node-svc-001-registry',
  NODE_LIFECYCLE: 'rtwo-node-svc-002-lifecycle',
  NODE_CONFIGURATION: 'rtwo-node-svc-003-configuration',
  CAPABILITY_REGISTRY: 'rtwo-node-svc-004-capability',
  NODE_ASSIGNMENT: 'rtwo-node-svc-005-assignment',
  HEALTH_SOURCE_REGISTRY: 'rtwo-node-svc-006-health',
  NODE_DISCOVERY: 'rtwo-node-svc-007-discovery',
  PROVISIONING: 'rtwo-node-svc-008-provisioning',
  EVENT_PUBLISHER: 'rtwo-node-svc-009-events',
  READINESS: 'rtwo-node-svc-010-readiness',
} as const;

// ─────────────────────────────────────────────
// NODE EVENTS
// ─────────────────────────────────────────────

export type NodeEventType =
  | 'node_created'
  | 'node_configured'
  | 'node_provisioned'
  | 'node_ready'
  | 'node_connected'
  | 'node_synchronizing'
  | 'node_operational'
  | 'node_maintenance'
  | 'node_retired'
  | 'capability_enabled'
  | 'capability_disabled'
  | 'assignment_created'
  | 'assignment_revoked'
  | 'health_source_registered'
  | 'configuration_applied';

export interface NodeEvent {
  eventId: string;
  eventType: NodeEventType;
  nodeId: NodeId;
  timestamp: NodeTimestamp;
  payload: Record<string, unknown>;
}
